import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, Pencil, Loader2, Trash2, Building2, X, Plus, Link2, ShieldCheck } from "lucide-react";

const ROLE_STYLES = { admin: "bg-[#FBE6E6] text-[#C0392B]", director: "bg-[#E3F3ED] text-[#1A365D]", family: "bg-[#EFEEE8] text-[#5C6660]" };

export function UsersManager() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async (search) => {
    setLoading(true);
    try { const { data } = await api.get("/admin/users", { params: { search: search || undefined } }); setUsers(data); }
    catch { toast.error("Failed to load users"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSearch = (e) => { e?.preventDefault(); load(q); };
  const patchUser = (u) => setUsers((list) => list.map((x) => x.user_id === u.user_id ? u : x));

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? Any facilities they manage will revert to unclaimed.`)) return;
    try { await api.delete(`/admin/users/${u.user_id}`); toast.success("User deleted"); setUsers((l) => l.filter((x) => x.user_id !== u.user_id)); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete user"); }
  };

  return (
    <div>
      <p className="text-[#4A534E] mb-4">Manage user accounts, their login email &amp; role, and which facilities they control. Reassign a listing when a director changes.</p>
      <form onSubmit={onSearch} className="flex gap-2 max-w-xl mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5C6660]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="um-search" placeholder="Search by name or email..." className="pl-9 rounded-xl h-11" />
        </div>
        <Button type="submit" className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11" data-testid="um-search-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}</Button>
      </form>

      {users.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#E8E6DF] text-[#5C6660]">{loading ? "Loading..." : "No users found."}</div>
      ) : (
        <div className="space-y-2" data-testid="um-results">
          {users.map((u) => (
            <div key={u.user_id} className="bg-white border border-[#E8E6DF] rounded-xl p-4 flex items-center justify-between gap-3" data-testid="um-user-row">
              <div className="min-w-0">
                <div className="font-medium text-[#1C211F] truncate flex items-center gap-2">
                  {u.name || "—"}
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${ROLE_STYLES[u.role] || ROLE_STYLES.family}`}>{u.role}</span>
                </div>
                <div className="text-xs text-[#5C6660] truncate">{u.email}</div>
                {u.facilities.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {u.facilities.map((f) => (
                      <span key={f.facility_id} className="inline-flex items-center gap-1 text-[11px] bg-[#F7F6F2] border border-[#E8E6DF] rounded-full px-2 py-0.5 text-[#1A365D]">
                        <Building2 className="h-3 w-3" /> {f.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button onClick={() => setEditing(u)} variant="outline" size="sm" className="rounded-xl border-[#E8E6DF]" data-testid="um-edit-btn"><Pencil className="h-4 w-4 mr-1.5" /> Manage</Button>
                <Button onClick={() => removeUser(u)} variant="outline" size="sm" className="rounded-xl border-[#E8E6DF] text-[#C0392B]" data-testid="um-delete-btn"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <ManageDialog user={editing} onClose={() => setEditing(null)} onSaved={patchUser} />}
    </div>
  );
}

function ManageDialog({ user, onClose, onSaved }) {
  const [u, setU] = useState(user);
  const [form, setForm] = useState({ email: user.email || "", name: user.name || "", phone: user.phone || "", role: user.role || "family" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Facility link search
  const [fq, setFq] = useState("");
  const [facResults, setFacResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const saveProfile = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/admin/users/${u.user_id}`, form); setU(data); onSaved(data); toast.success("User updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update user"); } finally { setSaving(false); }
  };

  const searchFacilities = async (e) => {
    e?.preventDefault();
    setSearching(true);
    try { const { data } = await api.get("/facilities", { params: { search: fq || undefined, limit: 25 } }); setFacResults(data.facilities || []); }
    catch { toast.error("Search failed"); } finally { setSearching(false); }
  };

  const link = async (f) => {
    try { const { data } = await api.post(`/admin/users/${u.user_id}/link/${f.facility_id}`); setU(data); onSaved(data); toast.success(`Linked ${f.name}`); setFacResults([]); setFq(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not link facility"); }
  };

  const unlink = async (f) => {
    if (!window.confirm(`Remove ${u.name || u.email}'s control of "${f.name}"? It reverts to claimable.`)) return;
    try { const { data } = await api.delete(`/admin/users/${u.user_id}/link/${f.facility_id}`); setU(data); onSaved(data); toast.success("Unlinked"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not unlink"); }
  };

  const linkedIds = new Set(u.facilities.map((f) => f.facility_id));

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto" data-testid="um-manage-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-[#0077B6]" /> Manage user</DialogTitle>
          <DialogDescription>Edit the account (login email &amp; role) and control which facilities this user manages.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label htmlFor="um-name">Name</Label><Input id="um-name" data-testid="um-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            <div className="space-y-1.5">
              <Label htmlFor="um-role">Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger id="um-role" className="rounded-xl h-10" data-testid="um-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="family">Family</SelectItem>
                  <SelectItem value="director">Director</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5"><Label htmlFor="um-email">Login email</Label><Input id="um-email" type="email" data-testid="um-email" value={form.email} onChange={(e) => set("email", e.target.value)} /><p className="text-xs text-[#5C6660]">This is the email they sign in with (separate from a facility's public contact email).</p></div>
          <div className="space-y-1.5"><Label htmlFor="um-phone">Phone</Label><Input id="um-phone" data-testid="um-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></div>
          <Button onClick={saveProfile} disabled={saving} className="bg-[#1A365D] hover:bg-[#12283F] text-white rounded-xl h-11" data-testid="um-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save account"}
          </Button>

          <div className="border-t border-[#E8E6DF] pt-4">
            <h3 className="font-heading text-lg font-medium text-[#1C211F] mb-2 flex items-center gap-2"><Building2 className="h-5 w-5 text-[#0077B6]" /> Facilities managed</h3>
            {u.facilities.length === 0 ? (
              <p className="text-sm text-[#5C6660] mb-3">Not linked to any facility yet.</p>
            ) : (
              <div className="space-y-2 mb-3" data-testid="um-linked-facilities">
                {u.facilities.map((f) => (
                  <div key={f.facility_id} className="flex items-center justify-between gap-2 bg-[#F7F6F2] border border-[#E8E6DF] rounded-xl px-3 py-2">
                    <div className="min-w-0"><div className="text-sm font-medium text-[#1C211F] truncate">{f.name}</div><div className="text-xs text-[#5C6660]">{f.city} · <span className="capitalize">{f.tier}</span></div></div>
                    <Button onClick={() => unlink(f)} variant="ghost" size="sm" className="text-[#C0392B] hover:bg-[#FBE6E6] rounded-lg shrink-0" data-testid="um-unlink-btn"><X className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}

            <Label htmlFor="um-facility-search" className="flex items-center gap-1.5 text-sm"><Link2 className="h-4 w-4" /> Link a facility</Label>
            <form onSubmit={searchFacilities} className="flex gap-2 mt-1.5">
              <Input id="um-facility-search" value={fq} onChange={(e) => setFq(e.target.value)} data-testid="um-facility-search" placeholder="Facility name or city..." className="rounded-xl h-10" />
              <Button type="submit" variant="outline" className="rounded-xl h-10 border-[#E8E6DF]" data-testid="um-facility-search-btn">{searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find"}</Button>
            </form>
            {facResults.length > 0 && (
              <div className="mt-2 space-y-1.5 max-h-52 overflow-y-auto" data-testid="um-facility-results">
                {facResults.map((f) => (
                  <div key={f.facility_id} className="flex items-center justify-between gap-2 bg-white border border-[#E8E6DF] rounded-xl px-3 py-2">
                    <div className="min-w-0"><div className="text-sm text-[#1C211F] truncate">{f.name}</div><div className="text-xs text-[#5C6660]">{f.city || f.county}{f.claimed ? " · currently claimed" : ""}</div></div>
                    {linkedIds.has(f.facility_id)
                      ? <span className="text-xs text-[#1A7F5A] shrink-0">Linked</span>
                      : <Button onClick={() => link(f)} size="sm" className="bg-[#0077B6] hover:bg-[#005f93] text-white rounded-lg shrink-0" data-testid="um-link-btn"><Plus className="h-4 w-4 mr-1" /> Link</Button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
